import { makeStyles } from '@material-ui/core/styles'
import { NextSeo } from 'next-seo'
import React, { useState } from 'react'
import { GetServerSideProps } from 'next'
import dynamic from 'next/dynamic'
import { Amplitude, LogOnMount } from '@amplitude/react-amplitude'

import { Filters } from '../types/general'
import { urls, tabs } from '../src/utils/config'
import FiltersProvider from '../src/contexts/Filters'
import { CryptoCard as ICryptoCard } from '../types/cryptoCard'
import { getCryptoCards } from '../src/services/cards'
import { Region } from '../types/region'
import { getRegions } from '../src/services/regions'

const SeoText = dynamic(() => import('../src/components/SeoText/CryptoCards'))
import Background from '../src/components/Subnav/Background'
const Description = dynamic(() => import('../src/components/Subnav/Description'))
const CryptoCardFilters = dynamic(() => import('../src/components/Filters/CryptoCard'))
const CryptoCard = dynamic(() => import('../src/components/Card/CryptoCard'))
import Nav from '../src/components/Nav'
import Footer from '../src/components/Footer'

const useStyles = makeStyles((theme) => ({
  root: {
    maxWidth: '950px',
    margin: 'auto',
  },
  exchangeContainer: {
    marginBottom: '65px',
  },
  error: {
    padding: '50px',
    margin: '50px',
  },
  tableHead: {
    padding: '0 20px',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    '& h6': {
      color: theme.palette.primary.main,
      width: '100px',
    },
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
}))

interface SavingAccountsContainerProps {
  cryptoCards: ICryptoCard[]
  regions: Region[]
}

function SavingAccountsContainer({
  cryptoCards: initialCryptoCards,
  regions,
}: SavingAccountsContainerProps): JSX.Element {
  const classes = useStyles({})
  const filteredInitialCryptoCards = initialCryptoCards.filter((obj) =>
    obj.regions.find((obj) => obj.name === 'Europe'),
  )
  const [selectedCryptoCards, setSelectedCryptoCards] = useState<ICryptoCard[]>(
    filteredInitialCryptoCards,
  )

  function onFilterCards(filters: Filters) {
    const filteredRegion = filters.region
    if (filteredRegion) {
      const newCards = initialCryptoCards.filter((obj) =>
        obj.regions.find((obj) => obj.name === filteredRegion.name),
      )
      setSelectedCryptoCards(newCards)
    }
  }

  return (
    <Amplitude
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      eventProperties={(inheritedProps: { page: any }) => ({
        ...inheritedProps,
        page: {
          ...inheritedProps.page,
          name: urls.cryptoCards,
        },
      })}
    >
      {(): JSX.Element => (
        <FiltersProvider>
          <LogOnMount eventType="page-displayed" />
          <NextSeo
            title={`The Best Crypto Cards`}
            description={`Spend your cryptocurrency in stores or online using a conventional payment card. You won’t need a bank account anymore.`}
            openGraph={{
              url: `https://cryptotesters.com${urls.cryptoCards}`,
              title: `The Best Crypto Cards`,
              description: `Spend your cryptocurrency in stores or online using a conventional payment card. You won’t need a bank account anymore.`,
              images: [
                {
                  url: 'https://cryptotesters.com/sharing.png',
                  width: 1200,
                  height: 630,
                  alt: `Cryptotesters.com`,
                },
                {
                  url: 'https://cryptotesters.com/sharable_small.png',
                  width: 600,
                  height: 400,
                  alt: `Cryptotesters.com Logo`,
                },
              ],
              site_name: 'Cryptotesters.com',
            }}
            twitter={{
              handle: '@cryptotesters',
              site: '@cryptotesters',
              cardType: 'summary_large_image',
            }}
          />
          <Nav />
          <div className={classes.root}>
            <Background />
            <Description selectedTab={tabs.cryptoCards} />
            <CryptoCardFilters regions={regions} onFilter={onFilterCards} />
            {selectedCryptoCards.map((obj, index) => (
              <CryptoCard cryptoCard={obj} key={index} />
            ))}
            <SeoText />
          </div>
          <Footer />
        </FiltersProvider>
      )}
    </Amplitude>
  )
}

export const getServerSideProps: GetServerSideProps = async () => {
  let cryptoCards: ICryptoCard[] = []
  let regions: Region[] = []

  try {
    cryptoCards = await getCryptoCards()
    regions = await getRegions()
  } catch (e) {
    console.error(e)
  }

  return {
    props: {
      cryptoCards,
      regions,
    },
  }
}

export default SavingAccountsContainer
